import { z } from 'zod'

// ── Calc trace ─────────────────────────────────────────────────────────────────

export const calcTraceSchema = z.object({
  tool_id: z.string(),
  summary: z.string(),
  rendered_sql: z.string().nullable(),
})

export type CalcTrace = z.infer<typeof calcTraceSchema>

// ── Policy citation ────────────────────────────────────────────────────────────

export const policyCitationSchema = z.object({
  policy_id: z.guid(),
  version: z.number().int(),
  rule_code: z.string(),
  prose_en: z.string(),
})

export type PolicyCitation = z.infer<typeof policyCitationSchema>

// ── Explanation AI provenance ─────────────────────────────────────────────────
// Named with an `Explanation` prefix to avoid conflict with `AiProvenance`
// in packages/shared-types/src/ai.ts (which is a broader shape used by
// the InsightsAIDigest and WhyPanel surfaces).
//
// Shape mirrors services/api/src/routes/right_to_explanation.py::AiProvenanceOut

export const explanationAiProvenanceSchema = z.object({
  model_id: z.string().nullable(),
  prompt_version: z.string().nullable(),
  input_hash: z.string().nullable(),
  confidence: z.number().nullable(),
  override_flag: z.boolean().nullable(),
  override_reason: z.string().nullable(),
})

export type ExplanationAiProvenance = z.infer<typeof explanationAiProvenanceSchema>

// ── Explanation response ───────────────────────────────────────────────────────

export const decidedBySchema = z.object({
  employee_id: z.guid(),
  name: z.string(),
  role: z.string(),
})

export type DecidedBy = z.infer<typeof decidedBySchema>

export const explanationResponseSchema = z.object({
  leave_id: z.guid(),
  decision: z.enum(['approved', 'rejected', 'escalated', 'pending', 'cancelled']),
  decided_by: decidedBySchema.nullable(),
  decided_at: z.string().nullable(),
  calc_traces: z.array(calcTraceSchema),
  policy_citations: z.array(policyCitationSchema),
  ai_provenance: explanationAiProvenanceSchema.nullable(),
  /** Direct narrative text from the AI advisor. Populated once the AI
   *  advisor has processed the leave (ai_advisor_status = 'ready').
   *  This is the field to use for the ILMU panel body text. */
  ai_rationale_text: z.string().nullable(),
  /** AI advisor verdict: 'approve' | 'concern' | 'discuss'. Distinct from
   *  `decision` (the leave status). Use this for the ILMU panel verdict label. */
  ai_advisor_verdict: z.string().nullable(),
})

export type ExplanationResponse = z.infer<typeof explanationResponseSchema>
